//
// File: Role.ts
// Description: Type definition for a Location - a location of a company store
// Copyright: Broadlume 2024
//

/**
 * Role type, representing a role within a Company
 */
export interface Role {
  /**
   * The unique identifier for the role
   * Optional when creating a new role as it will be generated by the server
   */
  id?: string;
  /**
   * The name of the role
   */
  name: string;
  /**
   * The human friendly description of the role
   */
  description?: string;
}

/**
 * Mapping of user role keys to their labels
 */
export const USER_ROLE_MAP = {
  ACCOUNT_USER: 'Account User',
  ACCOUNT_ADMIN: 'Account Admin',
  SYSTEMS_USER: 'Systems User',
  SYSTEMS_ADMIN: 'Systems Admin',
} as const;

/**
 * Type representing a valid user role key
 * Constrains values to keys defined in USER_ROLE_MAP
 */
export type UserRoleKey = keyof typeof USER_ROLE_MAP;

/**
 * Type representing a user role display value
 */
export type UserRoleValue = (typeof USER_ROLE_MAP)[UserRoleKey];

/**
 * Reverse mapping of user role display values to their API keys
 */
const USER_ROLE_REVERSE_MAP = Object.fromEntries(
  Object.entries(USER_ROLE_MAP).map(([key, value]) => [value, key]),
) as Record<UserRoleValue, UserRoleKey>;

/**
 * Retrieves the API role key for a given role label
 * Converts display values back to API enum keys for form submissions and API calls.
 */
export const getUserRoleKey = (value: UserRoleValue): UserRoleKey => USER_ROLE_REVERSE_MAP[value];
